import Meta from '../components/Meta'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useParams } from 'react-router'
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Form,
  ListGroup,
} from 'react-bootstrap'
import {
  BoxArrowInRight,
  CheckCircleFill,
  ChevronLeft,
  Download,
  DropletFill,
  FileEarmark,
  FileEarmarkPlus,
  Person,
  PersonVideo3,
  Printer,
  SendCheck,
  Tag,
  UpcScan,
  Upload,
  X,
} from 'react-bootstrap-icons'
import { useNavigate } from 'react-router-dom'
import { useClient } from 'urql'
import {
  GetOrderShipmentLabelDocument,
  GetOrderShipmentLabelQuery,
  GetOrderShipmentLabelQueryVariables,
  Order_State_Enum_Enum,
  useDeleteOrderTestResultMutation,
  useGetOrderQuery,
  usePingMutation,
  useSendOrderTestMutation,
} from '../generated/urql.lab-user'
import { AuthContext } from '../provider/auth'
import BarCodeModal, { BarCodeModalType } from '../components/BarCodeModal'
import { OrderState2Label } from '../lib/order'
import CollectOrderTestModal, {
  CollectOrderTestModalType,
} from '../components/CollectOrderTestModal'
import { StorageUrl } from '../const'
import { FilenamePattern } from '../lib/testResult'
import TestResultConfirmationModal, {
  TestResultConfirmationModalType,
} from '../components/TestResultConfirmationModal'
import { ValidTestResult } from '../components/TestResultUploadModal'
import { OrderState2BgVariant } from '../lib/order'
import { isIdentity } from '../lib/identity'

const pageTitle = 'Szczegóły badania'

function renderIdentity(identity: unknown) {
  if (!isIdentity(identity)) {
    return 'Nieznany rodzaj tożsamości'
  }

  switch (identity.type) {
    case 'id_card':
      return (
        <span>
          {identity.value}{' '}
          <small>
            (dowód tożsamości, {identity.countryCode}, {identity.dateOfBirth})
          </small>
        </span>
      )
    case 'passport':
      return (
        <span>
          {identity.value}{' '}
          <small>
            (paszport, {identity.countryCode}, {identity.dateOfBirth})
          </small>
        </span>
      )
    case 'pesel':
      return (
        <span>
          {identity.value} <small>(PESEL)</small>
        </span>
      )
  }
}

const Order: FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const auth = useContext(AuthContext)
  const urqlClient = useClient()
  const [{ data, error }, refetchGetOrder] = useGetOrderQuery({
    variables: { id: parseInt(id!) },
    requestPolicy: 'network-only',
  })
  const barCodeModalRef = useRef<BarCodeModalType>(null)
  const collectOrderTestModalRef = useRef<CollectOrderTestModalType>(null)
  const testResultConfirmationModalRef =
    useRef<TestResultConfirmationModalType>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [generalError, setGeneralError] = useState<string>()
  const [, deleteOrderTestResult] = useDeleteOrderTestResultMutation()
  const [, ping] = usePingMutation()
  const [checkedOrderTestIds, setCheckedOrderTestIds] = useState<number[]>([])
  const [{ fetching: sendOrderTestFetching }, sendOrderTest] =
    useSendOrderTestMutation()
  const [documents, setDocuments] = useState<string[]>([])
  const [printClientKey, setPrintClientKey] = useState<string | null>(null)

  // Funkcja do tworzenia unikalnego klucza klienta
  const getClientKey = useCallback(
    (orderTest: {
      firstName: string
      lastName: string
      identity?: unknown
    }) => {
      const identityStr =
        orderTest.identity && isIdentity(orderTest.identity)
          ? JSON.stringify(orderTest.identity)
          : ''
      return `${orderTest.firstName}|${orderTest.lastName}|${identityStr}`
    },
    []
  )

  // Grupowanie badań po klientach
  const testsByClient = useMemo(() => {
    if (!data?.order_by_pk?.orderTests) return new Map()

    const grouped = new Map<
      string,
      {
        clientKey: string
        firstName: string
        lastName: string
        identity: unknown
        tests: typeof data.order_by_pk.orderTests
      }
    >()

    data.order_by_pk.orderTests.forEach((orderTest) => {
      const clientKey = getClientKey(orderTest)
      if (!grouped.has(clientKey)) {
        grouped.set(clientKey, {
          clientKey,
          firstName: orderTest.firstName,
          lastName: orderTest.lastName,
          identity: orderTest.identity,
          tests: [],
        })
      }
      grouped.get(clientKey)!.tests.push(orderTest)
    })

    return grouped
  }, [data?.order_by_pk?.orderTests, getClientKey])

  // Funkcja do drukowania badań dla konkretnego klienta
  const printClientTests = useCallback((clientKey: string) => {
    setPrintClientKey(clientKey)
    setTimeout(() => {
      window.print()
      setPrintClientKey(null)
    }, 100)
  }, [])

  const downloadShipmentLabel = useCallback(
    async (shipmentId: number, trackingNumber?: string | null) => {
      const fileName = trackingNumber ? `${trackingNumber}.pdf` : 'etykieta.pdf'
      const { data, error } = await urqlClient
        .query<GetOrderShipmentLabelQuery, GetOrderShipmentLabelQueryVariables>(
          GetOrderShipmentLabelDocument,
          { shipmentId }
        )
        .toPromise()

      if (error || !data?.lab_user_order_shipment_label?.fileBase64) {
        setGeneralError('Nie udało się pobrać etykiety')
        return
      }

      const { fileBase64, fileType } = data.lab_user_order_shipment_label
      if (fileBase64 && fileType === 'pdf') {
        const bytes = atob(fileBase64)
        const arr = new Uint8Array(bytes.length)
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
        const blob = new Blob([arr], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.click()
        URL.revokeObjectURL(url)
      }
    },
    [urqlClient]
  )

  const downloadFile = useCallback(
    async (id: number) => {
      const response = await fetch(`${StorageUrl}/files/${id}`, {
        headers: {
          Authorization: `Bearer ${auth.accessToken}`,
        },
      })

      const blob = await response.blob()
      return window.URL.createObjectURL(blob)
    },
    [auth.accessToken]
  )

  const openFile = useCallback(
    async (id: number) => {
      try {
        window.location.assign(await downloadFile(id))
      } catch (error) {
        console.error(error)
      }
    },
    [downloadFile]
  )

  useEffect(() => {
    if (data?.order_by_pk) {
      Promise.all(
        data.order_by_pk.documents.map(async (document) =>
          downloadFile(document.fileId)
        )
      )
        .then(setDocuments)
        .catch(console.error)
    }
  }, [data?.order_by_pk, downloadFile])

  async function uploadOrderTestResult(
    orderTests: ValidTestResult['orderTests'],
    files: FileList
  ) {
    setGeneralError(undefined)

    await ping({}, { requestPolicy: 'network-only' })

    if (orderTests.length === 1) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const matches = file.name.match(FilenamePattern)
        if (matches) {
          const [, tubeCode, , laboratoryCode] = matches
          if (
            orderTests[0].tubeCode === tubeCode ||
            orderTests[0].laboratoryCode === laboratoryCode
          ) {
            continue
          }
        }

        setGeneralError(`Nieprawidłowa nazwa pliku ${file.name}`)
        return
      }
    }

    testResultConfirmationModalRef.current?.show(
      [...files].map((file) => ({
        file,
        orderTests,
      }))
    )
  }

  const backURL = useMemo(() => {
    if (!data?.order_by_pk) {
      return '/'
    } else if (
      data.order_by_pk.orderTests.every(
        (orderTest) =>
          orderTest.collectionPoint?.laboratoryId === auth.laboratoryId
      )
    ) {
      if (
        data.order_by_pk.orderTests.every(
          (orderTest) => orderTest.testResults.length > 0
        )
      ) {
        return '/finished'
      } else {
        return '/accepted'
      }
    } else if (
      data.order_by_pk.orderTests.find(
        (orderTest) =>
          orderTest.collectionPoint?.laboratoryId === auth.laboratoryId
      )
    ) {
      return '/'
    } else {
      return '/'
    }
  }, [data])

  return (
    <div>
      <Meta title={pageTitle} />
      <style>{`
        @media print {
          .client-section:not(.print-client) {
            display: none !important;
          }
          .client-section.print-client {
            display: block !important;
          }
          .client-section.print-client .d-print-none {
            display: none !important;
          }
          .client-section.print-client h5 {
            margin-bottom: 1rem;
          }
        }
      `}</style>
      <div className="mt-3 mb-3 d-flex justify-content-between d-print-none">
        <ButtonGroup>
          <Button
            className="d-flex align-items-center"
            onClick={() => navigate(backURL)}
          >
            <ChevronLeft size={20} />
            &nbsp;Wróć
          </Button>
        </ButtonGroup>
      </div>
      {(error || generalError) && (
        <Alert variant="danger">{generalError || error?.message}</Alert>
      )}
      {data?.order_by_pk && (
        <>
          <div className="d-flex">
            <div className="flex-grow-1">
              <h4 className="mt-5">Informacje</h4>
              <p>
                <span className="fw-bold">Nr zlecenia: </span>
                {data.order_by_pk.id}
              </p>
              {data.order_by_pk.orderCollections[0] && (
                data.order_by_pk.orderCollections[0].isVoucher ? (
                  <p>
                    <span className="fw-bold">Voucher ważny do: </span>
                    {new Date(
                      data.order_by_pk.orderCollections[0].voucherValidUntil
                    ).toLocaleString()}
                  </p>
                ) : (
                  !!data.order_by_pk.orderCollections[0].collectionDate && (
                    <p>
                      <span className="fw-bold">Data zlecenia: </span>
                      {new Date(
                        data.order_by_pk.orderCollections[0].collectionDate
                      ).toLocaleString()}
                    </p>
                  )
                )
              )}
              {data.order_by_pk.orderCollections[0] && (
                <p>
                  <span className="fw-bold">Osoba pobierająca: </span>
                  {data.order_by_pk.nurse?.name}
                </p>
              )}
              <p>
                <span className="fw-bold">Status: </span>
                <Badge
                  bg={
                    OrderState2BgVariant[
                      data.order_by_pk.currentState as Order_State_Enum_Enum
                    ]
                  }
                >
                  {
                    OrderState2Label[
                      data.order_by_pk.currentState as Order_State_Enum_Enum
                    ]
                  }
                </Badge>
              </p>

              {!!data.order_by_pk.orderCollections[0]?.nurseNotes && (
                <>
                  <h4 className="mt-5">Notatki osoby pobierającej</h4>
                  <p style={{ whiteSpace: 'pre' }}>
                    {data.order_by_pk.orderCollections[0].nurseNotes}
                  </p>
                </>
              )}
            </div>
            {data.order_by_pk.orderCollections[0] && (
              <div className="flex-grow-1">
                <h4 className="mt-5">Miejsce pobrania</h4>
                <p>
                  <span className="fw-bold">Adres: </span>
                  {data.order_by_pk.orderCollections[0].address},{' '}
                  {data.order_by_pk.orderCollections[0].postalCode}{' '}
                  {data.order_by_pk.orderCollections[0].city}
                </p>
                <p>
                  <span className="fw-bold">Numer telefonu: </span>
                  {data.order_by_pk.orderCollections[0].phoneNumber}
                </p>
              </div>
            )}
          </div>
          {documents.length > 0 && (
            <>
              <h4 className="mt-5">Dokumenty pacjenta</h4>
              <ul className="d-flex list-unstyled">
                {documents.map((uri) => (
                  <li className="me-2">
                    <a href={uri} target="_blank" rel="noreferrer">
                      <img src={uri} alt="" className="img-thumbnail" />
                    </a>
                  </li>
                ))}
              </ul>
            </>
          )}
          {!!data?.order_by_pk?.orderShippings?.length && (
            <>
              <h4 className="mt-5">Wysyłki</h4>
              <ListGroup className="mb-3">
                {data.order_by_pk.orderShippings.map((s) => (
                  <ListGroup.Item
                    key={s.id}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <strong>
                        {s.direction === 'to_lab'
                          ? 'Do laboratorium'
                          : 'Do klienta'}
                      </strong>
                      {s.pointName && (
                        <span className="ms-2 text-muted">{s.pointName}</span>
                      )}
                      {s.trackingNumber && (
                        <span className="ms-2 text-muted">
                          #{s.trackingNumber}
                        </span>
                      )}
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="d-flex align-items-center"
                      onClick={() =>
                        downloadShipmentLabel(s.id, s.trackingNumber)
                      }
                    >
                      <Download size={16} className="me-1" />
                      Pobierz etykietę
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
          <h4 className="mt-5">Badania</h4>
          {Array.from(testsByClient.values()).map((clientGroup) => {
            const isPrintingThisClient =
              printClientKey === clientGroup.clientKey
            return (
              <div
                key={clientGroup.clientKey}
                className={`client-section ${
                  isPrintingThisClient ? 'print-client' : ''
                }`}
                data-client-key={clientGroup.clientKey}
              >
                <div className="d-flex justify-content-between align-items-center mb-3 d-print-none">
                  <h5 className="mb-0">
                    {clientGroup.firstName} {clientGroup.lastName}
                    {isIdentity(clientGroup.identity) && (
                      <small className="text-muted ms-2">
                        ({renderIdentity(clientGroup.identity)})
                      </small>
                    )}
                  </h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="d-flex align-items-center"
                    onClick={() => printClientTests(clientGroup.clientKey)}
                  >
                    <Printer size={16} className="me-1" />
                    Drukuj listę badań
                  </Button>
                </div>
                <ListGroup className="w-75 w-print-100 mb-4">
                  {clientGroup.tests.map(
                    (orderTest: (typeof clientGroup.tests)[0]) => (
                      <ListGroup.Item
                        className="d-flex justify-content-between align-items-center"
                        key={orderTest.id}
                      >
                        <Form.Check
                          className="me-1 d-print-none"
                          disabled={
                            !!orderTest.finishedOn || !orderTest.retrievedOn
                          }
                          checked={checkedOrderTestIds.includes(orderTest.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCheckedOrderTestIds([
                                ...checkedOrderTestIds,
                                orderTest.id,
                              ])
                            } else {
                              setCheckedOrderTestIds(
                                checkedOrderTestIds.filter(
                                  (orderTestId) => orderTestId !== orderTest.id
                                )
                              )
                            }
                          }}
                        />
                        <div className="ms-2 me-auto w-100">
                          <div className="fw-bold mb-2">
                            {orderTest.test.name}
                          </div>
                          {orderTest.test.labInstructions && (
                            <pre className="text-muted font-sans">
                              {orderTest.test.labInstructions}
                            </pre>
                          )}
                          <div className="d-flex align-items-center">
                            <span className="text-muted w-25">Klient:</span>
                            <Person size={20} className="me-1" />
                            <span>
                              {orderTest.firstName} {orderTest.lastName}
                            </span>
                          </div>
                          <div className="d-flex align-items-center">
                            <span className="text-muted w-25">
                              Dokument tożsamości:
                            </span>
                            <PersonVideo3 size={20} className="me-1" />
                            {renderIdentity(orderTest.identity)}
                          </div>
                          {orderTest.tubeCode && (
                            <div className="d-flex align-items-center">
                              <span className="text-muted w-25">
                                Kod probówki:
                              </span>
                              <UpcScan size={20} className="me-1" />
                              <span>{orderTest.tubeCode}</span>
                            </div>
                          )}
                          {orderTest.laboratoryCode && (
                            <div className="d-flex align-items-center">
                              <span className="text-muted w-25">
                                Kod laboratorium:
                              </span>
                              <Tag size={20} className="me-1" />
                              <span>{orderTest.laboratoryCode}</span>
                            </div>
                          )}
                          {!!orderTest.testResults.length &&
                            orderTest.testResults.map(
                              (
                                testResult: (typeof orderTest.testResults)[0]
                              ) => (
                                <div
                                  className="d-flex align-items-center"
                                  key={testResult.file.id}
                                >
                                  <span className="text-muted w-25">
                                    Wynik:
                                  </span>
                                  <FileEarmark size={20} className="me-1" />
                                  <Button
                                    onClick={() => openFile(testResult.file.id)}
                                    className="p-0"
                                    variant="link"
                                  >
                                    {testResult.originalFilename}
                                  </Button>
                                  {!orderTest.finishedOn && (
                                    <Button
                                      variant="link"
                                      className="p-0 d-print-none"
                                      onClick={async () => {
                                        if (
                                          window.confirm(
                                            'Czy napewno chcesz usunąć wynik badania?'
                                          )
                                        ) {
                                          const { error } =
                                            await deleteOrderTestResult({
                                              fileId: testResult.file.id,
                                              orderTestId: orderTest.id,
                                            })
                                          if (error) {
                                            setGeneralError(error.message)
                                            return
                                          }

                                          refetchGetOrder({
                                            requestPolicy: 'network-only',
                                          })
                                        }
                                      }}
                                    >
                                      <X size={22} color="#f56565" />
                                    </Button>
                                  )}
                                </div>
                              )
                            )}
                          {orderTest.collectedOn && (
                            <div
                              className="d-flex align-items-center"
                              title="Data pobrania"
                            >
                              <span className="text-muted w-25">Pobrano:</span>
                              <DropletFill size={20} className="me-1" />
                              <span>
                                {new Date(
                                  orderTest.collectedOn
                                ).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {orderTest.retrievedOn && (
                            <div
                              className="d-flex align-items-center"
                              title="Data pobrania"
                            >
                              <span className="text-muted w-25">Odebrano:</span>
                              <BoxArrowInRight size={20} className="me-1" />
                              <span>
                                {new Date(
                                  orderTest.retrievedOn
                                ).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {orderTest.finishedOn && (
                            <div
                              className="d-flex align-items-center"
                              title="Data wysłania wyniku do klienta"
                            >
                              <span className="text-muted w-25">Wysłano:</span>
                              <CheckCircleFill size={20} className="me-1" />
                              <span>
                                {new Date(
                                  orderTest.finishedOn
                                ).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                        {!orderTest.collectionPoint && (
                          <Button
                            className="ms-2 d-flex align-items-center d-print-none"
                            onClick={() => {
                              if (barCodeModalRef?.current) {
                                barCodeModalRef.current.show({
                                  expectedBarcode:
                                    orderTest.tubeCode || undefined,
                                  orderTestId: orderTest.id,
                                  orderId: orderTest.order.id,
                                  currentOrderState:
                                    data.order_by_pk
                                      ?.currentState as Order_State_Enum_Enum,
                                  isShipping:
                                    orderTest.test.fulfillment === 'shipping',
                                })
                              }
                            }}
                            variant="warning"
                          >
                            <UpcScan size={20} />
                          </Button>
                        )}
                        {orderTest.retrievedOn &&
                          !orderTest.finishedOn && (
                            <div className="d-flex align-items-center d-print-none">
                              <Button
                                title="Dodaj wynik"
                                className="ms-2 d-flex align-items-center"
                                onClick={(event) => {
                                  event.currentTarget
                                    .querySelectorAll('input[type=file]')
                                    .forEach((input: any) => input.click())
                                }}
                              >
                                <FileEarmarkPlus size={20} />
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  className="visually-hidden"
                                  multiple
                                  accept="application/pdf"
                                  onChange={(event) => {
                                    if (event.target.files?.length) {
                                      event.target.files?.length &&
                                        uploadOrderTestResult(
                                          [orderTest],
                                          event.target.files
                                        )
                                    }
                                  }}
                                />
                              </Button>
                            </div>
                          )}
                        {orderTest.collectionPoint &&
                          !orderTest.finishedOn &&
                          !orderTest.collectedOn && (
                            <Button
                              title="Kod probówki"
                              className="ms-2 d-flex align-items-center d-print-none"
                              onClick={() => {
                                if (collectOrderTestModalRef?.current) {
                                  collectOrderTestModalRef.current.show(
                                    orderTest.id,
                                    orderTest.tubeCode || undefined
                                  )
                                }
                              }}
                            >
                              <DropletFill size={20} />
                            </Button>
                          )}
                        {!orderTest.finishedOn &&
                          orderTest.testResults.length > 0 && (
                            <Button
                              title="Wyślij wyniki do pacjenta"
                              variant="success"
                              disabled={sendOrderTestFetching}
                              className="ms-2 d-flex align-items-center d-print-none"
                              onClick={async () => {
                                if (
                                  window.confirm(
                                    'Czy napewno chcesz wysłać wyniki badania do pacjenta?'
                                  )
                                ) {
                                  const { error } = await sendOrderTest({
                                    id: orderTest.id,
                                    now: new Date(),
                                  })
                                  if (error) {
                                    setGeneralError(error.message)
                                    return
                                  }

                                  refetchGetOrder({
                                    requestPolicy: 'network-only',
                                  })
                                }
                              }}
                            >
                              <SendCheck size={20} />
                              &nbsp;Wyślij
                            </Button>
                          )}
                      </ListGroup.Item>
                    )
                  )}
                </ListGroup>
              </div>
            )
          })}
          {checkedOrderTestIds.length > 0 && (
            <Button
              title="Prześlij wyniki dla wybranych badań"
              className="mt-2 d-flex align-items-center d-print-none"
              onClick={(event) => {
                event.currentTarget
                  .querySelectorAll('input[type=file]')
                  .forEach((input: any) => input.click())
              }}
            >
              <Upload size={20} className="me-2" />
              Prześlij wyniki dla wybranych badań
              <input
                ref={fileInputRef}
                type="file"
                className="visually-hidden"
                multiple
                accept="application/pdf"
                onChange={async (event) => {
                  if (event.target.files?.length && data.order_by_pk) {
                    await uploadOrderTestResult(
                      data.order_by_pk.orderTests.filter((orderTest) =>
                        checkedOrderTestIds.includes(orderTest.id)
                      ),
                      event.target.files
                    )

                    setCheckedOrderTestIds([])
                  }
                }}
              />
            </Button>
          )}
          <BarCodeModal
            ref={barCodeModalRef}
            onSuccess={() => {
              refetchGetOrder({ requestPolicy: 'network-only' })
            }}
          />
          <CollectOrderTestModal
            ref={collectOrderTestModalRef}
            onChange={() => {
              refetchGetOrder({ requestPolicy: 'network-only' })
            }}
          />
          <TestResultConfirmationModal
            ref={testResultConfirmationModalRef}
            onFinished={() => {
              refetchGetOrder({ requestPolicy: 'network-only' })
              if (fileInputRef.current) {
                fileInputRef.current.value = ''
              }
            }}
          />
        </>
      )}
    </div>
  )
}

export default Order
